import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { toast } from 'sonner';
import { DollarSign, ShoppingCart, Users, Camera, TrendingUp, Printer, Image, Package, Loader2, UserX } from 'lucide-react';

import { StatCard } from '@/components/admin/analytics/StatCard';
import { PurchaseNudgeCard } from '@/components/admin/analytics/PurchaseNudgeCard';
import { RevenueOverTimeChart } from '@/components/admin/analytics/RevenueOverTimeChart';
import { RevenueBreakdownPie } from '@/components/admin/analytics/RevenueBreakdownPie';
import { OrdersOverTimeChart } from '@/components/admin/analytics/OrdersOverTimeChart';
import { UserRolesCard } from '@/components/admin/analytics/UserRolesCard';
import { BuyerBehaviorCard } from '@/components/admin/analytics/BuyerBehaviorCard';
import { ReminderStatsCard } from '@/components/admin/analytics/ReminderStatsCard';
import { ParentEngagementCard } from '@/components/admin/analytics/ParentEngagementCard';
import { TopPhotosTable } from '@/components/admin/analytics/TopPhotosTable';
import { RecentOrdersTable } from '@/components/admin/analytics/RecentOrdersTable';
import { logError } from '@/lib/logger';

import { API } from '@/lib/apiBase';

export default function AdminAnalytics() {
  const { getAuthHeaders } = useAuth();
  const { t, language } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nudgePreview, setNudgePreview] = useState(null);
  const [nudgeDaysBefore, setNudgeDaysBefore] = useState(10);
  const [sendingNudges, setSendingNudges] = useState(false);
  const [period, setPeriod] = useState('all');
  const [school, setSchool] = useState('all');
  const [classId, setClassId] = useState('all');
  const [classes, setClasses] = useState([]);

  const fetchClasses = async () => {
    try {
      const res = await fetch(`${API}/classes`, { headers: getAuthHeaders(), credentials: 'include' });
      if (res.ok) setClasses(await res.json());
    } catch (e) { logError('AdminAnalytics.fetchClasses', e); }
  };

  const fetchNudgePreview = useCallback(async (daysBefore = nudgeDaysBefore) => {
    try {
      const res = await fetch(`${API}/admin/purchase-nudge-preview?days_before=${daysBefore}`, {
        headers: getAuthHeaders(), credentials: 'include',
      });
      if (res.ok) setNudgePreview(await res.json());
    } catch (e) { logError('AdminAnalytics.nudgePreview', e); }
  // getAuthHeaders + nudgeDaysBefore are stable enough — Session 50 slider fix.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nudgeDaysBefore]);

  const handleSendPurchaseNudges = async () => {
    if (!nudgePreview || nudgePreview.outstanding === 0) {
      toast.info(t('analytics.nudgeNoneOutstanding'));
      return;
    }
    setSendingNudges(true);
    try {
      const res = await fetch(`${API}/admin/send-purchase-nudges?days_before=${nudgeDaysBefore}&lang=${language}`, {
        method: 'POST', headers: getAuthHeaders(), credentials: 'include',
      });
      if (!res.ok) throw new Error();
      const result = await res.json();
      toast.success(`${result.will_send} ${t('analytics.nudgeQueued')}`);
      setTimeout(fetchNudgePreview, 2500);
    } catch (e) {
      toast.error(t('common.error'));
    } finally {
      setSendingNudges(false);
    }
  };

  // Unique list of schools derived from classes. Sorted alphabetically.
  // ClassModel field is `school_name` (not `school`).
  const schools = useMemo(() => {
    const set = new Set();
    classes.forEach((c) => { if (c.school_name) set.add(c.school_name); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [classes]);

  // Classes narrowed by the picked school so the two dropdowns stay in sync.
  const scopedClasses = useMemo(() => {
    const list = school === 'all' ? classes : classes.filter((c) => c.school_name === school);
    return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [classes, school]);

  const handleSchoolChange = (v) => {
    setSchool(v);
    setClassId('all'); // reset class scope when school changes
  };

  useEffect(() => { fetchClasses(); }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentional: mount-only fetch

  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      try {
        const qs = new URLSearchParams({ period });
        if (school !== 'all') qs.set('school', school);
        if (classId !== 'all') qs.set('class_id', classId);
        const res = await fetch(`${API}/admin/analytics?${qs.toString()}`, {
          headers: getAuthHeaders(),
          credentials: 'include',
        });
        if (res.ok) setData(await res.json());
      } catch (e) {
        logError('AdminAnalytics.Analyticsfetcherror', e);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
    fetchNudgePreview();
  }, [getAuthHeaders, period, school, classId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="analytics-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid="analytics-error">
        <p className="text-muted-foreground">{t('common.error')}</p>
      </div>
    );
  }

  const s = data.summary;
  const monthlyData = data.monthly_data.map((m) => ({
    ...m,
    label: `${m.month.slice(5)}/${m.month.slice(2, 4)}`,
  }));

  const revenueBreakdown = [
    { name: t('analytics.digital'), value: s.digital_revenue },
    { name: t('analytics.prints'), value: s.print_revenue },
    { name: t('analytics.bundles'), value: s.bundle_revenue },
  ].filter((d) => d.value > 0);

  const roleData = Object.entries(data.user_roles || {}).map(([role, count]) => ({
    name: t(`analytics.role_${role}`) || role,
    value: count,
  }));

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid="admin-analytics">
      <div className="mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2">{t('analytics.title')}</h1>
          <p className="text-muted-foreground">{t('analytics.description')}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label htmlFor="school-select" className="text-sm text-muted-foreground">
            {t('analytics.schoolLabel')}:
          </label>
          <select
            id="school-select"
            value={school}
            onChange={(e) => handleSchoolChange(e.target.value)}
            className="bg-card border border-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            data-testid="school-select"
          >
            <option value="all">{t('analytics.allSchools')}</option>
            {schools.map((sch) => (<option key={sch} value={sch}>{sch}</option>))}
          </select>

          <label htmlFor="class-select" className="text-sm text-muted-foreground">
            {t('analytics.classLabel')}:
          </label>
          <select
            id="class-select"
            value={classId}
            onChange={(e) => setClassId(e.target.value)}
            className="bg-card border border-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            data-testid="class-select"
          >
            <option value="all">{t('analytics.allClasses')}</option>
            {scopedClasses.map((c) => (
              <option key={c.class_id} value={c.class_id}>{c.name} ({c.year})</option>
            ))}
          </select>

          <label htmlFor="period-select" className="text-sm text-muted-foreground">
            {t('analytics.periodLabel')}:
          </label>
          <select
            id="period-select"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="bg-card border border-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            data-testid="period-select"
          >
            <option value="all">{t('analytics.period_all')}</option>
            <option value="month">{t('analytics.period_month')}</option>
            <option value="3months">{t('analytics.period_3months')}</option>
            <option value="6months">{t('analytics.period_6months')}</option>
            <option value="year">{t('analytics.period_year')}</option>
            <option value="last_year">{t('analytics.period_last_year')}</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <StatCard icon={DollarSign} label={t('analytics.totalRevenue')} value={`CHF ${s.total_revenue.toFixed(2)}`} />
        <StatCard icon={ShoppingCart} label={t('analytics.paidOrders')} value={s.paid_orders} sub={`${s.pending_orders} ${t('analytics.pending')}`} />
        <StatCard icon={Users} label={t('analytics.totalUsers')} value={s.total_users} />
        <StatCard icon={Camera} label={t('analytics.totalPhotos')} value={s.total_photos} />
      </div>

      {/* Session 52: non-buyers tile — parents in current scope with zero paid
          orders. Reacts to the class/school filter above so admins can spot
          under-converting classes at a glance. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <StatCard
          icon={UserX}
          label={t('analytics.nonBuyers')}
          value={typeof s.non_buyers === 'number' ? s.non_buyers : '—'}
          sub={t('analytics.nonBuyersSub')}
        />
      </div>

      <PurchaseNudgeCard
        nudgePreview={nudgePreview}
        sending={sendingNudges}
        onSend={handleSendPurchaseNudges}
        daysBefore={nudgeDaysBefore}
        onDaysBeforeChange={(v) => { setNudgeDaysBefore(v); fetchNudgePreview(v); }}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
        <RevenueOverTimeChart monthlyData={monthlyData} />
        <RevenueBreakdownPie revenueBreakdown={revenueBreakdown} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
        <OrdersOverTimeChart monthlyData={monthlyData} />
        <UserRolesCard roleData={roleData} totalUsers={s.total_users} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
        <StatCard icon={Package} label={t('analytics.digital')} value={`CHF ${s.digital_revenue.toFixed(2)}`} />
        <StatCard icon={Printer} label={t('analytics.prints')} value={`CHF ${s.print_revenue.toFixed(2)}`} />
        <StatCard icon={TrendingUp} label={t('analytics.bundles')} value={`CHF ${s.bundle_revenue.toFixed(2)}`} />
        <StatCard icon={Image} label={t('dashboard.classes')} value={s.total_classes} />
        <StatCard icon={Users} label={t('dashboard.students')} value={s.total_students} />
      </div>

      {data.buyer_behavior && (
        <BuyerBehaviorCard buyerBehavior={data.buyer_behavior} paidOrders={s.paid_orders} />
      )}

      {data.reminder_stats && (
        <ReminderStatsCard reminderStats={data.reminder_stats} />
      )}

      {data.parent_engagement && (
        <ParentEngagementCard engagement={data.parent_engagement} />
      )}

      <TopPhotosTable topPhotos={data.top_photos} />
      <RecentOrdersTable recentOrders={data.recent_orders} />
    </div>
  );
}
